// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the app layer's view of the portal's mobile REST surface.
//
// UI → App → Data: nothing in `src/js/ui/mobile` calls `MobileClient` directly.
//
// ⚠ MobileClient is imported BY PATH, not through `home/data`. That barrel
// re-exports ApiCache and CacheManager, and a phone has no reason to download
// the desktop cache layer — the same rule `ui_tiles.ts` documents for ObjectTable.
//
// ⚠ Every route below is app-shaped (`todos`, `me`), never a PQFORCE path. The
// portal proxy is a NARROW ALLOW-LIST that builds each upstream URL itself from
// the device record — the tenant, and the caller's own `resourceId`, are never
// request parameters. A route here that took a raw endpoint would defeat that;
// see §3 of the implementation plan and `pqfClfGlobalStoreEndpoint()`, which
// makes the same argument for the same reason.
// ─────────────────────────────────────────────────────────────────────────────

import { MobileClient } from "home/data/MobileClient";
import { lifecycleCategoryOf, type NudgeResult, type NudgeSkipReason } from "home/app/mobile/todos";
import { priorityOf } from "home/app/mobile/priorities";

import {
    type Assignee,
    type LifecycleTransition,
    type PpmLink,
    type Priority,
    type RelatedLink,
    type TenantDiscovery,
    type Todo,
    type TodoComment,
    type TodoRelation,
} from "home/app/mobile/types";

/** Who the signed-in person is, as the portal resolved it from the tenant. */
export interface MobileProfile {
    /** `ACM/CurrentUser.name`. */
    name: string;
    email: string;
    /** `ACM/CurrentUser.resource.id` — what every PI and ACT call is rooted at. */
    resourceId: string;
    tenantHost: string;
    /**
     * Host plus alias — what the header shows.
     *
     * ⚠ A tenant is host AND alias, so `tenantHost` alone does not identify the installation when
     * two aliases share a server. Optional because a device signed in before this shipped has a
     * cached profile without it; the header falls back to the host.
     */
    tenantUrl?: string;
    /**
     * `de` or `en`: the PQFORCE account's language, which the app follows. Absent from an older
     * server, or for a device paired before it was stored — the app then keeps the language it has.
     */
    language?: string;
    tenantName: string;
}

export interface StartedPairing {
    enrollmentId: string;
    authorizationUrl: string;
    /** ISO 8601. The user has a bounded window to approve — surface it. */
    authorizationDeadline: string;
}

export interface PairingResult {
    accessToken: string;
    profile: MobileProfile;
}

export interface Person {
    id: string;
    name: string;
}

export interface ProjectOption {
    id: string;
    name: string;
}

/** What the composer needs before it can offer anything. */
export interface TodoCreateOptions {
    projects: ProjectOption[];
    people: Person[];
    /** Who a TODO can be handed over to — whom the owner relation accepts. Absent from an older server. */
    ownerPeople?: Person[];
    /** @deprecated The composer picks a date now; kept so an older server's payload still parses. */
    dueOptions?: string[];
    /**
     * @deprecated An older server sent the priorities it would accept. They are a fixed list now —
     * `TODO_PRIORITIES` — so this is ignored; kept so that server's payload still parses.
     */
    priorities?: string[];
    /**
     * What the tenant calls the two roles (the bound relation types' names, in the app's language).
     * Empty or absent where it has none — see app/mobile/relationNames.ts.
     */
    relationNames?: { assignee?: string; owner?: string };
    /** The same relation types named from the person's side — the names of their two lists. */
    relationListNames?: { assignee?: string; owner?: string };
}

export interface NewTodoInput {
    title: string;
    projectId: string;
    assigneeId: string;
    /**
     * ⚠ A DATE, `YYYY-MM-DD`, or `""` for none — not one of the old `Today`/`This week`/`Later`
     * labels. Those could not express any other day, and left the server deciding what "this week"
     * meant against its own calendar rather than the reader's.
     */
    dueISO: string;
    /** A `TODO_PRIORITIES` id, or `""` for none. */
    priority: Priority | "";
}

/** The wire shape of a todo. Kept separate from `Todo` so a server change is caught here. */
interface TodoPayload {
    id: string;
    itemType: string;
    relation: TodoRelation;
    relations?: TodoRelation[];
    partial?: boolean;
    code?: string;
    permissions?: { update?: unknown; link?: unknown; delete?: unknown } | null;
    status: string;
    statusId?: string;
    statusCategory?: string | null;
    statusColor?: string | null;
    statusOrder?: number | null;
    title: string;
    description?: string;
    descriptionText?: string;
    descriptionEditable?: boolean;
    ppm?: PpmLink | null;
    related?: RelatedLink[];
    /** @deprecated Derived from `ppm` on the server, for one release. */
    project?: string;
    /** @deprecated Derived from `ppm` on the server, for one release. */
    task?: string;
    assignees?: Assignee[];
    /** @deprecated One string for an older bundle; the server derives it from `assignees`. */
    assignee?: string;
    owners?: Array<string | { id?: string; name?: string }>;
    owner?: string;
    due?: string;
    dueISO?: string;
    /** @deprecated Derived on the device from `dueISO`, for one release. */
    dueBucket?: string;
    overdue?: boolean;
    /** A `TODO_PRIORITIES` id, or null. An older server sent free text from a property binding. */
    priority?: string | null;
    comments?: TodoComment[];
    transitions?: LifecycleTransition[];
}

/**
 * Wire → domain.
 *
 * Defensive on every optional field, because these values originate in a
 * customer's own tenant configuration: a PI item with no description, no linked
 * task and no assignee is ordinary, and must render rather than throw.
 *
 * ⚠ Every category is run through `lifecycleCategoryOf()` — on the todo, on each
 * transition's target and on each history endpoint — so nothing downstream meets
 * a spelling outside PQFORCE's enum and has to guess what it meant.
 */
export function toTodo(payload: TodoPayload): Todo {
    return {
        id: payload.id,
        itemType: payload.itemType,
        relation: payload.relation,
        // A payload from before the server carried the list still has one relation, and it is
        // the one it named.
        relations: payload.relations ?? [payload.relation],
        status: payload.status,
        // ⚠ An older server sends none of these three. Grouping then falls back to the name, the
        // chip to the neutral paint, and the order to alphabetical — all visible, none a guess.
        statusId: payload.statusId ?? "",
        statusCategory: lifecycleCategoryOf(payload.statusCategory),
        statusColor: payload.statusColor ?? null,
        statusOrder:
            typeof payload.statusOrder === "number" && Number.isFinite(payload.statusOrder)
                ? payload.statusOrder
                : null,
        title: payload.title,
        description: payload.description ?? "",
        descriptionText: payload.descriptionText ?? "",
        // ⚠ Strictly `=== true`: absent means an older server, which is not a promise that the
        // value is plain. See the field's own note in types.ts.
        descriptionEditable: payload.descriptionEditable === true,
        // ⚠ ABSENT AND NULL MEAN THE SAME THING HERE: no link. `undefined` is a server that
        // predates the field, `null` is one that looked and found nothing, and a reader cannot act
        // on the difference — so both become null rather than the field being made optional, which
        // would push the same choice out to every render site.
        ppm: payload.ppm ?? null,
        related: payload.related ?? [],
        project: payload.project ?? "",
        task: payload.task ?? "—",
        // An older server sends one `assignee` name and no ids. Shown as it came, with no id — so it
        // reads correctly and cannot be addressed by an add, a remove or a nudge.
        assignees: Array.isArray(payload.assignees)
            ? payload.assignees
                  .filter((a) => a && typeof a.id === "string" && a.id !== "")
                  .map((a) => ({ id: a.id, name: String(a.name ?? "") }))
            : payload.assignee
              ? [{ id: "", name: payload.assignee }]
              : [],
        // ⚠ THREE SHAPES, because a phone outlives a deploy: `{id,name}` from today's server, bare
        // names from yesterday's, and `owner` alone from the one before that. An entry with no id
        // matches nobody, which is the right answer — it simply shows a name instead of "You".
        owners: (payload.owners ?? (payload.owner ? [payload.owner] : [])).map((o) =>
            typeof o === "string" ? { id: "", name: o } : { id: String(o.id ?? ""), name: String(o.name ?? "") },
        ),
        owner: payload.owner ?? "",
        // ⚠ ONE stored value. The label, the group and the overdue flag are derived on the DEVICE,
        // against the device's own clock — see `dueFacts()`. The server still sends the three
        // computed fields for one release, for a phone running an older bundle; this one ignores
        // them, because they are the server's opinion of what "today" means.
        dueISO: payload.dueISO ?? "",
        // ⚠ NULL, not a neutral priority, when the item has no marker — or one this app does not
        // know (an older server sent `High`/`Normal`/`Low` from a property binding).
        priority: priorityOf(payload.priority)?.id ?? null,
        comments: (payload.comments ?? []).map((comment) => ({
            ...comment,
            fromCategory: lifecycleCategoryOf(comment.fromCategory),
            toCategory: lifecycleCategoryOf(comment.toCategory),
        })),
        transitions: (payload.transitions ?? []).map((transition) => ({
            ...transition,
            toCategory: lifecycleCategoryOf(transition.toCategory),
        })),
        ...(payload.partial === true ? { partial: true } : {}),
        code: typeof payload.code === "string" ? payload.code : "",
        // ⚠ Strictly `=== true` per action: anything else the server sent is not a grant.
        ...(payload.permissions && typeof payload.permissions === "object"
            ? {
                  permissions: {
                      update: payload.permissions.update === true,
                      link: payload.permissions.link === true,
                      delete: payload.permissions.delete === true,
                  },
              }
            : {}),
    };
}

export class MobileApi {
    constructor(private readonly client: MobileClient) {}

    // ── onboarding ───────────────────────────────────────────────────────────

    /**
     * Ask the portal which sign-in methods an installation offers.
     *
     * The address the user typed is sent WHOLE (alias included): the host decides
     * discovery, but `ACM/ApiToken/Create?tenantalias=` needs the alias, and
     * asking for it twice is how the two come to disagree.
     */
    discover(tenantUrl: string): Promise<TenantDiscovery> {
        return this.client.post<TenantDiscovery>("auth/discover", { tenantUrl });
    }

    /**
     * Begin pairing: the portal mints an unauthorised token on the tenant and
     * hands back the URL the user must approve it at.
     *
     * ⚠ The raw `apiToken` is NOT in this response and must never be — it stays
     * server-side against the pending enrollment.
     */
    startPairing(tenantUrl: string, methodId: string): Promise<StartedPairing> {
        return this.client.post<StartedPairing>("auth/start", { tenantUrl, methodId });
    }

    /**
     * Poll for approval. Resolves once the tenant reports the token authorised
     * and the portal has read the profile behind it.
     */
    completePairing(enrollmentId: string): Promise<PairingResult> {
        return this.client.post<PairingResult>("auth/complete", { enrollmentId });
    }

    /**
     * Sign out of the ACTIVE installation only.
     *
     * Revokes the token on that tenant and deletes its device record; any other installation this
     * device is paired with is untouched. `remaining` says how many are left, so the app can put
     * the person on one of them rather than on an address field they have already filled in.
     */
    signOut(): Promise<{ remaining: number }> {
        return this.client.post<{ remaining: number }>("auth/logout");
    }

    /**
     * Switch to an installation this device is ALREADY paired with.
     *
     * ⚠ No tenant call and no re-approval — the pairing lives in the device cookie and this only
     * decides which of them is active. A 404 means the pairing is gone (or was never made on this
     * device), and the caller answers that by pairing rather than by showing a failure.
     */
    switchTenant(tenantUrl: string): Promise<PairingResult> {
        return this.client.post<PairingResult>("auth/switch", { tenantUrl });
    }

    // ── session ──────────────────────────────────────────────────────────────

    me(): Promise<MobileProfile> {
        return this.client.get<MobileProfile>("me");
    }

    // ── todos ────────────────────────────────────────────────────────────────

    /**
     * Both relations in one call.
     *
     * Deliberately not one request per relation: the home screen needs both
     * counts before it can draw, and two round trips on a phone's first paint is
     * two chances to be slow.
     */
    async todos(): Promise<Todo[]> {
        const payload = await this.client.get<TodoPayload[]>("todos");
        return payload.map(toTodo);
    }

    /** Every TODO this person may see — the list's "All TODOs". Rows are `partial`. */
    async allTodos(): Promise<Todo[]> {
        const payload = await this.client.get<TodoPayload[]>("todos/all");
        return payload.map(toTodo);
    }

    /**
     * Make one of a TODO's link slots hold exactly these targets — `owner` with one person is a
     * hand-over. A person's type is the server's business; an object slot (later) sends it.
     */
    async setLinks(
        id: string,
        slot: "assignee" | "owner",
        targets: Array<{ id: string; type?: string }>,
    ): Promise<Todo> {
        return toTodo(await this.client.put<TodoPayload>(`todos/${encodeURIComponent(id)}/links/${slot}`, { targets }));
    }

    /** Delete a TODO — the server refuses unless PQFORCE grants this person DELETE on it. */
    async deleteTodo(id: string): Promise<void> {
        await this.client.request<unknown>("DELETE", `todos/${encodeURIComponent(id)}`);
    }

    /** One TODO, whole — with what this person may do to it (`permissions`), read when it is opened. */
    async todo(id: string): Promise<Todo> {
        return toTodo(await this.client.get<TodoPayload>(`todos/${encodeURIComponent(id)}`));
    }

    /**
     * Fire a lifecycle transition.
     *
     * Returns the todo as the tenant now holds it — including its NEW transition
     * set, which only the server can know. Recomputing that locally is exactly
     * the mistake `types.ts` warns about.
     */
    async fireTransition(todoId: string, transitionId: string, note: string): Promise<Todo> {
        const payload = await this.client.post<TodoPayload>(`todos/${encodeURIComponent(todoId)}/transition`, {
            transitionId,
            note,
        });
        return toTodo(payload);
    }

    async comment(todoId: string, text: string): Promise<Todo> {
        const payload = await this.client.post<TodoPayload>(`todos/${encodeURIComponent(todoId)}/comment`, {
            text,
        });
        return toTodo(payload);
    }

    /**
     * Add ONE assignee. The others are left as they are — a TODO may have several.
     * Adding somebody already assigned changes nothing and still answers with the TODO.
     */
    async assign(todoId: string, personId: string): Promise<Todo> {
        const payload = await this.client.post<TodoPayload>(`todos/${encodeURIComponent(todoId)}/assign`, {
            personId,
        });
        return toTodo(payload);
    }

    /** Remove ONE assignee. Removing somebody who is not assigned changes nothing. */
    async unassign(todoId: string, personId: string): Promise<Todo> {
        const payload = await this.client.post<TodoPayload>(`todos/${encodeURIComponent(todoId)}/unassign`, {
            personId,
        });
        return toTodo(payload);
    }

    /**
     * Change or clear a TODO's due date.
     *
     * ⚠ Owner only, and the server says so — this is a date that makes something overdue for
     * somebody else. `""` clears it; the route reads an empty string as a deliberate absence.
     */
    async setDue(todoId: string, dueISO: string): Promise<Todo> {
        const payload = await this.client.post<TodoPayload>(`todos/${encodeURIComponent(todoId)}/due`, { dueISO });

        return toTodo(payload);
    }

    /**
     * Change or clear a TODO's priority — its `marker`. Owner only, and the server says so.
     *
     * `""` clears it: most TODOs carry none, so "none" is a value an owner must be able to set. The
     * server answers with the TODO as the tenant now holds it, and refuses when the change did not
     * take — so what comes back is always the truth, not an echo of the request.
     */
    async setPriority(todoId: string, priority: Priority | ""): Promise<Todo> {
        const payload = await this.client.post<TodoPayload>(`todos/${encodeURIComponent(todoId)}/priority`, {
            priority,
        });

        return toTodo(payload);
    }

    /**
     * Rename a TODO and rewrite its description. Owner only, and the server says so.
     *
     * ⚠ `description` is sent only when it is being changed — a key the route does not receive is a
     * field it does not touch, so renaming cannot wipe a description. The server refuses outright
     * when the stored value carries formatting a textarea would flatten.
     */
    async setDetails(todoId: string, title: string, description?: string): Promise<Todo> {
        const payload = await this.client.post<TodoPayload>(
            `todos/${encodeURIComponent(todoId)}/details`,
            description === undefined ? { title } : { title, description },
        );

        return toTodo(payload);
    }

    /**
     * Remind every assignee but the caller, by EMAIL. Writes nothing to the lifecycle.
     *
     * Answers with the resource ids actually emailed and, per person skipped, why — so the toast names
     * both rather than claiming everybody was reminded. An older server answers only `addressees`.
     */
    async nudge(todoId: string): Promise<NudgeResult> {
        const output = await this.client.post<{ addressees?: unknown; skipped?: unknown } | null>(
            `todos/${encodeURIComponent(todoId)}/nudge`,
        );
        const ids = output?.addressees;
        const skipped = Array.isArray(output?.skipped) ? (output!.skipped as unknown[]) : [];
        const REASONS: readonly NudgeSkipReason[] = ["optedOut", "noAddress", "noAccount", "failed"];

        return {
            emailed: Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [],
            skipped: skipped.flatMap((entry) => {
                const e = entry as { resourceId?: unknown; reason?: unknown };
                return typeof e?.resourceId === "string" && REASONS.includes(e.reason as NudgeSkipReason)
                    ? [{ resourceId: e.resourceId, reason: e.reason as NudgeSkipReason }]
                    : [];
            }),
        };
    }

    async create(input: NewTodoInput): Promise<Todo> {
        const payload = await this.client.post<TodoPayload>("todos", input);
        return toTodo(payload);
    }

    /** What the New TODO sheet may offer. Read once per session, then cached by the store. */
    createOptions(): Promise<TodoCreateOptions> {
        return this.client.get<TodoCreateOptions>("todos/options");
    }
}
